// The **unverified** badge (DC-05; 05 §DE configuration "shown as unverified on the DE's profile
// to everyone who can view the DE"; ADR-0019 §2 "the UI shows the unverified badge on it and on
// the DE profile"). One look wherever a DE, a deploy or an approval runs without a verification
// skill: a pilot under an operator's verification override, or an empty self-serve agent. It is
// derived from the DE mirror, the bundle or the approval's proposal, never from a flag a run sets.
import { Badge } from "@companyos/ui";
import { formatDate } from "../format";

export type Pilot = { pilot_until: string; expired?: boolean } | null | undefined;

/** What the badge's tooltip and screen-reader text say about the pilot, if there is one. */
export function pilotNote(pilot: Pilot): string | null {
  if (!pilot) return null;
  return pilot.expired
    ? `Pilot ended ${formatDate(pilot.pilot_until)}`
    : `Pilot until ${formatDate(pilot.pilot_until)}`;
}

/**
 * A DE running a bundle that nothing verifies (the mirror says `unverified` and it has a deploy;
 * a DE never deployed through AiGentPanel shows no badge).
 */
export function deployedUnverified(de: {
  deploy_ref: string | null;
  verification_state: "verified" | "unverified";
}): boolean {
  return de.deploy_ref !== null && de.verification_state === "unverified";
}

export function UnverifiedBadge({ pilot }: { pilot?: Pilot }) {
  const note = pilotNote(pilot);
  return (
    <Badge
      variant="warning"
      data-slot="unverified-badge"
      title={note ? `Unverified · ${note}` : "Unverified: no verification skill checks its output"}
    >
      Unverified
      {note ? <span className="sr-only"> ({note})</span> : null}
    </Badge>
  );
}
